import { useNavigate, useParams } from "react-router-dom";

import NotFound from "../../NotFound";
import { useContext, useEffect, useReducer } from "react";
import { Auth as AuthContext } from "../../../contexts/auth/index";
import PaintPage from "./components/paint";
import ConfigPage from "../../../components/config/";
import GalleryPage from "./components/gallery";
import { toast } from "sonner";
import axios from "axios";
import _config from "../../../config";
import { Path } from "../../../constants";
import { Errors } from "../../../utils/error";
import { zPaintEdit } from "../../../zod/paint";

type TPage = "paint" | "config" | "gallery";
type TState = {
    paint: TPaint | null;
    message: {
        type: "info" | "error";
        content: string;
    } | null;
    cantSubmit: boolean;
    isloading: boolean;
    page: TPage;
};

type PaintAction = {
    type: "paint";
    paint: TPaint;
    submit: boolean;
};
type SubmitAction = {
    type: "submit";
    submit: boolean;
};
type MessageAction = {
    type: "message";
    message: {
        type: "info" | "error";
        content: string;
    } | null;
};
type LoaderAction = {
    type: "loader";
    loader: boolean;
};
type PageAction = {
    type: "page";
    page: TPage;
};
type ConfigAction = {
    type: "config";
    config: Config;
    submit: boolean;
};

type Action = PaintAction | MessageAction | LoaderAction | PageAction | ConfigAction | SubmitAction;

const reducer = (state: TState, action: Action) => {
    if (action.type === "paint") {
        state.paint = action.paint;
        state.cantSubmit = action.submit;
    } else if (action.type === "page") {
        state.page = action.page;
    } else if (action.type === "config" && state.paint) {
        state.paint.config = action.config;
        state.cantSubmit = action.submit;
    } else if (action.type === "loader") {
        state.isloading = action.loader;
    } else if (action.type === "submit") {
        state.cantSubmit = action.submit;
    } else {
        toast.error("unknown action");
    }
    return { ...state };
};
const pages: TPage[] = ["paint", "config", "gallery"];

const Paint = () => {
    const [state, dispatch] = useReducer(reducer, {
        cantSubmit: false,
        isloading: false,
        message: null,
        paint: null,
        page: (window.location.hash.replace("#", "") || "paint") as TPage,
    });

    const paint = state.paint;
    const { user, token } = useContext(AuthContext);
    const { paintId } = useParams();
    const navigate = useNavigate();

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        try {
            event.preventDefault();
            dispatch({
                type: "loader",
                loader: true,
            });
            if (!paint || !user || !token) return toast.error("error to find paint or user or token");

            if (!paintId) return toast.error("error to find paintid");
            const data = zPaintEdit.parse(paint);

            const responce = await axios.put(`${_config.api_server_uri}${Path.Admin.paint.id.replace(/:paintid/, paintId)}`, data, { headers: { Authorization: token } });
            if (responce.status !== 200) return; // get message
            toast.success(responce.data.message, { position: "top-center" });
        } catch (error) {
            Errors.handler(error);
        } finally {
            dispatch({
                type: "loader",
                loader: false,
            });
            dispatch({
                type: "submit",
                submit: false,
            });
        }
    };

    useEffect(() => {
        if (user && paintId) {
            const paint = user.paints.find((paint) => paint.id === paintId);
            if (paint) {
                dispatch({
                    type: "paint",
                    paint,
                    submit: false,
                });
            }
        } else {
            toast.error("error to find user or paint_id");
        }
    }, [paintId, user]);

    useEffect(() => {
        navigate(`#${state.page}`);
    }, [state.page, navigate]);

    if (!user || !paint) return <NotFound />;

    return (
        <div className="h-full bg-gray-800">
            <div className="w-screen overflow-x-hidden h-full">
                <form className="h-max w-screen bg-gray-800" onSubmit={handleSubmit}>
                    {state.message && state.message.type === "info" && (
                        <div className="h-10 w-full flex items-center justify-center text-white pt-16 flex-col">
                            <div className="w-full sm:w-1/2">
                                <div id="alert-border-3" className="flex p-4 mb-4 border-t-4 text-green-400 bg-gray-900 border-green-800 rounded-xl">
                                    <svg className="flex-shrink-0 w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            fillRule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                            clipRule="evenodd"
                                        ></path>
                                    </svg>
                                    <div className="ml-3 text-sm font-medium">{state.message.content}</div>
                                    <button
                                        type="button"
                                        className="ml-auto -mx-1.5 -my-1.5  rounded-lg focus:ring-2 focus:ring-green-400 p-1.5inline-flex h-8 w-8 bg-gray-800 text-green-400 hover:bg-gray-700"
                                        onClick={() => {
                                            dispatch({ message: null, type: "message" });
                                        }}
                                    >
                                        <span className="sr-only">Dismiss</span>
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                fillRule="evenodd"
                                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                            ></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {state.message && state.message.type === "error" && (
                        <div className="h-10 w-full flex items-center justify-center text-white pt-16 flex-col">
                            <div className="w-full sm:w-1/2">
                                <div id="alert-border-3" className="flex p-4 mb-4 border-t-4 text-red-400 bg-red-900 border-red-800 rounded-xl">
                                    <svg className="flex-shrink-0 w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            fillRule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                            clipRule="evenodd"
                                        ></path>
                                    </svg>
                                    <div className="ml-3 text-sm font-medium">{state.message.content}</div>
                                    <button
                                        type="button"
                                        className="ml-auto -mx-1.5 -my-1.5  rounded-lg focus:ring-2 focus:ring-green-400 p-1.5 inline-flex h-8 w-8 bg-gray-800 text-green-400 hover:bg-gray-700"
                                        onClick={() => {
                                            dispatch({ message: null, type: "message" });
                                        }}
                                    >
                                        <span className="sr-only">Dismiss</span>
                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                fillRule="evenodd"
                                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                clipRule="evenodd"
                                            ></path>
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {state.cantSubmit && (
                        <div className="h-10 fixed top-28 right-8 z-40">
                            {state.isloading ? (
                                <div role="status" className=" flex w-20 h-20 justify-center items-center">
                                    <svg className="w-8 h-8 mr-2 animate-spin text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                            fill="currentColor"
                                        />
                                        <path
                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                            fill="currentFill"
                                        />
                                    </svg>
                                    <span className="sr-only">Loading...</span>
                                </div>
                            ) : (
                                <button
                                    type="submit"
                                    className="text-white focus:ring-4 font-medium rounded-lg text-sm px-5 py-2.5 mr-2 mb-2 bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-blue-800"
                                >
                                    edit paint
                                </button>
                            )}
                        </div>
                    )}
                    <div className="absolute top-0 right-0 w-36 h-36 flex items-center justify-center z-30">
                        <div className="flex items-center justify-center ">
                            <div className="m-5">
                                <div className="flex p-2.5 transition-all duration-300 text-white rounded-xl bg-gray-800 hover:bg-gray-700 cursor-pointer" onClick={() => navigate("/admin")}>
                                    <svg className="h-10 w-10" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"></path>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-full h-auto flex justify-center items-center flex-col py-16">
                        <div className="flex w-1/2 gap-5 text-white mb-5 ml-5">
                            {pages.map((page, index) => (
                                <div key={index}>
                                    <span
                                        className={`cursor-pointer w-full`}
                                        onClick={() => {
                                            dispatch({
                                                type: "page",
                                                page,
                                            });
                                        }}
                                    >
                                        {page}
                                        {page === state.page ? <span className="block w-full h-[2px] bg-white"></span> : null}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="bg-gray-900 w-full xl:w-1/2 rounded-none xl:rounded-lg p-5">
                            {state.page === "paint" ? (
                                <PaintPage
                                    paint={paint}
                                    onChange={(paint) => {
                                        dispatch({ type: "paint", paint, submit: true });
                                    }}
                                />
                            ) : null}

                            {state.page === "config" ? (
                                <ConfigPage
                                    config={paint.config}
                                    onChange={(config) => {
                                        dispatch({ type: "config", config, submit: true });
                                    }}
                                />
                            ) : null}
                            {state.page === "gallery" ? (
                                <GalleryPage
                                    paint={paint}
                                    onChange={(paint) => {
                                        dispatch({
                                            type: "paint",
                                            paint,
                                            submit: true,
                                        });
                                    }}
                                />
                            ) : null}
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Paint;
